/**
 * 游戏账号相关 Store
 * 负责账号数据的加载、管理、搜索和操作
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useCacheStore } from '../cache'
import { accountsApi } from '@/https/api/accounts'
import { marketApi } from '@/https/api/market'
import type {
    AccountCreateRequest,
    AccountUpdateRequest,
    AccountDetail,
    AccountListItem,
    AccountListRequest,
    AccountSearchRequest,
    AccountStatusEnum,
    AccountListResponse,
    AccountStats
} from '@/https/types/accounts'

export const useAccountsStore = defineStore('accounts', () => {
    const cacheStore = useCacheStore()

    // ==================== 状态管理 ====================

    // 账号列表
    const accountList = ref<AccountListItem[]>([])

    // 分页信息
    const pagination = ref<{
        total: number
        page: number
        size: number
        has_more: boolean
    }>({
        total: 0,
        page: 1,
        size: 20,
        has_more: false
    })

    // 查询参数
    const lastSearchQuery = ref<AccountSearchRequest>({})

    // 当前账号详情
    const currentAccount = ref<AccountDetail | null>(null)

    // 账号统计数据（来自后端API的全局统计，包含用户所有账号的统计信息）
    const accountStats = ref<AccountStats>({
        total_accounts: 0,
        published_accounts: 0,
        draft_accounts: 0,
        rented_accounts: 0,
        pending_accounts: 0,
        rejected_accounts: 0,
        pending_confirm_accounts: 0,
        locked_accounts: 0,
        await_final_accounts: 0
    })

    // 加载状态
    const accountListLoading = ref(false)
    const accountDetailLoading = ref(false)
    const accountOperationLoading = ref(false)
    const accountStatsLoading = ref(false)

    // 错误信息
    const accountListError = ref<string | null>(null)
    const accountStatsError = ref<string | null>(null)

    const fetchMarketAccountsWithRetry = async (searchData: AccountSearchRequest) => {
        try {
            return await marketApi.searchAccounts(searchData)
        } catch (error: unknown) {
            const requestError = error as {
                status?: number
                isNetworkError?: boolean
                isTimeout?: boolean
            }
            const status = Number(requestError?.status || 0)
            const isTransient = requestError?.isNetworkError
                || requestError?.isTimeout
                || status === 0
                || [502, 503, 504].includes(status)

            if (!isTransient) throw error

            await new Promise(resolve => window.setTimeout(resolve, 350))
            return marketApi.searchAccounts(searchData)
        }
    }

    // ==================== 计算属性 ====================

    // 是否有账号数据
    const hasAccountList = computed(() => accountList.value.length > 0)

    // 账号总数（当前页面分页信息中的总数）
    const accountCount = computed(() => pagination.value.total)

    // 当前页码
    const currentPage = computed(() => pagination.value.page)

    // 每页大小
    const pageSize = computed(() => pagination.value.size)

    // 是否有下一页
    const hasNextPage = computed(() => pagination.value.has_more)

    // 是否有上一页
    const hasPreviousPage = computed(() => pagination.value.page > 1)

    // 按状态分组的账号（仅当前页面数据，用于页面显示和筛选）
    const accountListByStatus = computed(() => {
        const groups: Record<string, AccountListItem[]> = {}
        accountList.value.forEach(account => {
            const status = account.status
            if (!groups[status]) {
                groups[status] = []
            }
            groups[status].push(account)
        })
        return groups
    })

    // 可租赁的账号（仅当前页面数据）
    const availableAccountList = computed(() => {
        return accountList.value.filter(account => account.status === 'available')
    })

    // 我的账号（需要结合当前用户）
    const myAccountList = computed(() => {
        // 这里应该根据当前用户ID过滤，暂时返回所有账号
        return accountList.value
    })

    // ==================== 数据加载 ====================

    /**
     * 加载账号列表
     * @param params 查询参数
     * @param forceRefresh 是否强制刷新
     */
    const loadAccountList = async (params?: AccountListRequest, forceRefresh = false): Promise<AccountListResponse> => {
        try {
            accountListLoading.value = true
            accountListError.value = null

            // 合并搜索参数，并保存为最近一次查询，以便后续刷新时复用
            const finalParams = { ...lastSearchQuery.value, ...params }
            lastSearchQuery.value = finalParams

            const data = await cacheStore.smartCache(
                `accounts:list:${JSON.stringify(finalParams)}`,
                () => accountsApi.getAccountList(finalParams),
                forceRefresh
            ) as AccountListResponse

            accountList.value = data.items || []
            pagination.value = {
                total: data.total || 0,
                page: data.page || 1,
                size: data.size || 20,
                has_more: data.has_more || false
            }

            return data
        } catch (error) {
            console.error('加载账号列表失败:', error)
            accountListError.value = error instanceof Error ? error.message : '加载账号列表失败'
            return {
                items: [],
                page: 1,
                size: 20,
                total: 0,
                has_more: false
            }
        } finally {
            accountListLoading.value = false
        }
    }

    /**
     * 搜索账号
     * @param searchData 搜索参数
     * @param forceRefresh 是否强制刷新
     */
    const searchAccounts = async (searchData: AccountSearchRequest, forceRefresh = false): Promise<AccountListResponse> => {
        try {
            accountListLoading.value = true
            accountListError.value = null

            // 保存搜索参数
            lastSearchQuery.value = searchData

            const data = await cacheStore.smartCache(
                `accounts:search:${JSON.stringify(searchData)}`,
                () => accountsApi.searchAccounts(searchData),
                forceRefresh
            ) as AccountListResponse

            accountList.value = data.items || []
            pagination.value = {
                total: data.total || 0,
                page: data.page || 1,
                size: data.size || 20,
                has_more: data.has_more || false
            }

            return data
        } catch (error) {
            console.error('搜索账号失败:', error)
            accountListError.value = error instanceof Error ? error.message : '搜索账号失败'
            return {
                items: [],
                page: 1,
                size: 20,
                total: 0,
                has_more: false
            }
        } finally {
            accountListLoading.value = false
        }
    }

    /**
     * 搜索市场账号（公开访问，无需登录）
     * @param searchData 搜索参数
     * @param forceRefresh 是否强制刷新
     * @param appendMode 是否追加模式（移动端无限滚动使用）
     */
    const searchMarketAccounts = async (searchData: AccountSearchRequest, forceRefresh = false, appendMode = false): Promise<AccountListResponse> => {
        try {
            const isAppend = !!(appendMode && searchData.page && searchData.page > 1)
            if (!isAppend) {
                accountListLoading.value = true
            }
            accountListError.value = null

            // 保存搜索参数
            lastSearchQuery.value = searchData

            const data = await cacheStore.smartCache(
                `market:search:${JSON.stringify(searchData)}`,
                () => fetchMarketAccountsWithRetry(searchData),
                forceRefresh
            ) as AccountListResponse

            if (isAppend) {
                // 追加模式：将新数据追加到现有列表
                accountList.value = [...accountList.value, ...(data.items || [])]
            } else {
                // 替换模式：替换整个列表（桌面端分页 / 移动端新搜索）
                accountList.value = data.items || []
            }

            pagination.value = {
                total: data.total || 0,
                page: data.page || 1,
                size: data.size || 20,
                has_more: data.has_more || false
            }

            return data
        } catch (error) {
            console.error('搜索市场账号失败:', error)
            const requestError = error as { message?: string }
            accountListError.value = requestError?.message || '搜索市场账号失败'
            return {
                items: accountList.value,
                page: pagination.value.page,
                size: pagination.value.size,
                total: pagination.value.total,
                has_more: pagination.value.has_more
            }
        } finally {
            const isAppend = !!(appendMode && searchData.page && searchData.page > 1)
            if (!isAppend) {
                accountListLoading.value = false
            }
        }
    }

    /**
     * 加载账号详情
     * @param id 账号ID
     * @param forceRefresh 是否强制刷新
     */
    const loadAccountDetail = async (id: number, forceRefresh = false): Promise<AccountDetail | null> => {
        try {
            accountDetailLoading.value = true
            accountListError.value = null

            const data = await cacheStore.smartCache(
                `accounts:detail:${id}`,
                () => accountsApi.getAccountDetail(id),
                forceRefresh
            )

            currentAccount.value = data
            return data
        } catch (error) {
            console.error('加载账号详情失败:', error)
            accountListError.value = error instanceof Error ? error.message : '加载账号详情失败'
            return null
        } finally {
            accountDetailLoading.value = false
        }
    }

    /**
     * 加载市场账号详情（公开访问，无需登录）
     * @param id 账号ID
     * @param forceRefresh 是否强制刷新
     */
    const loadMarketAccountDetail = async (id: number, forceRefresh = false): Promise<AccountDetail | null> => {
        try {
            accountDetailLoading.value = true
            accountListError.value = null

            const data = await cacheStore.smartCache(
                `market:detail:${id}`,
                () => marketApi.getAccountDetail(id),
                forceRefresh
            )

            currentAccount.value = data
            return data
        } catch (error) {
            console.error('加载市场账号详情失败:', error)
            accountListError.value = error instanceof Error ? error.message : '加载市场账号详情失败'
            return null
        } finally {
            accountDetailLoading.value = false
        }
    }

    // ==================== 数据操作 ====================

    /**
     * 创建账号
     * @param accountData 账号数据
     */
    const createAccount = async (accountData: AccountCreateRequest): Promise<AccountDetail> => {
        try {
            accountOperationLoading.value = true
            accountListError.value = null

            const createdAccount = await cacheStore.smartCache(
                'accounts:create',
                () => accountsApi.createAccount(accountData),
                true
            )

            // 刷新账号列表
            await loadAccountList(undefined, true)
            return createdAccount
        } catch (error) {
            console.error('创建账号失败:', error)
            accountListError.value = error instanceof Error ? error.message : '创建账号失败'

            // 抛出原始错误，让调用方能获取到详细的错误信息
            throw error
        } finally {
            accountOperationLoading.value = false
        }
    }

    /**
     * 更新账号
     * @param id 账号ID
     * @param accountData 账号数据
     */
    const updateAccount = async (id: number, accountData: AccountUpdateRequest): Promise<void> => {
        try {
            accountOperationLoading.value = true
            accountListError.value = null

            await cacheStore.smartCache(
                `accounts:update:${id}`,
                () => accountsApi.updateAccount(id, accountData)
            )

            // 清除详情缓存
            cacheStore.removeCache(`accounts:detail:${id}`)

            // 刷新账号列表
            await loadAccountList(undefined, true)

            // 如果当前正在查看这个账号的详情，重新加载
            if (currentAccount.value?.id === id) {
                await loadAccountDetail(id, true)
            }
        } catch (error) {
            console.error('更新账号失败:', error)
            accountListError.value = error instanceof Error ? error.message : '更新账号失败'

            // 抛出原始错误，让调用方能获取到详细的错误信息
            throw error
        } finally {
            accountOperationLoading.value = false
        }
    }

    /**
     * 删除账号
     * @param id 账号ID
     */
    const deleteAccount = async (id: number): Promise<boolean> => {
        try {
            accountOperationLoading.value = true
            accountListError.value = null

            await cacheStore.smartCache(
                `accounts:delete:${id}`,
                () => accountsApi.deleteAccount(id)
            )

            // 清除相关缓存
            cacheStore.removeCache(`accounts:detail:${id}`)
            cacheStore.removeCache('accounts:stats')

            // 从列表中移除
            accountList.value = accountList.value.filter(account => account.id !== id)

            // 刷新统计数据
            await loadAccountStats(true)

            // 如果当前正在查看这个账号的详情，清除
            if (currentAccount.value?.id === id) {
                currentAccount.value = null
            }

            return true
        } catch (error) {
            console.error('删除账号失败:', error)
            accountListError.value = error instanceof Error ? error.message : '删除账号失败'
            // 抛出错误，让调用方能够正确处理
            throw error
        } finally {
            accountOperationLoading.value = false
        }
    }

    /**
     * 提交审核
     * @param id 账号ID
     */
    const submitForAudit = async (id: number): Promise<boolean> => {
        try {
            accountOperationLoading.value = true
            accountListError.value = null

            await accountsApi.submitForAudit(id)

            cacheStore.removeCache(`accounts:detail:${id}`)
            cacheStore.removeCache('accounts:stats')

            await Promise.all([
                loadAccountList(undefined, true),
                loadAccountStats(true)
            ])

            if (currentAccount.value?.id === id) {
                await loadAccountDetail(id, true)
            }

            return true
        } catch (error) {
            console.error('提交审核失败:', error)
            accountListError.value = error instanceof Error ? error.message : '提交审核失败'
            throw error
        } finally {
            accountOperationLoading.value = false
        }
    }

    /**
     * 撤销审核
     * @param id 账号ID
     */
    const cancelAudit = async (id: number): Promise<boolean> => {
        try {
            accountOperationLoading.value = true
            accountListError.value = null

            await accountsApi.cancelAudit(id)

            cacheStore.removeCache(`accounts:detail:${id}`)
            cacheStore.removeCache('accounts:stats')

            await Promise.all([
                loadAccountList(undefined, true),
                loadAccountStats(true)
            ])

            if (currentAccount.value?.id === id) {
                await loadAccountDetail(id, true)
            }

            return true
        } catch (error) {
            console.error('撤销审核失败:', error)
            accountListError.value = error instanceof Error ? error.message : '撤销审核失败'
            throw error
        } finally {
            accountOperationLoading.value = false
        }
    }

    /**
     * 下架账号
     * @param id 账号ID
     */
    const unpublishAccount = async (id: number): Promise<boolean> => {
        try {
            accountOperationLoading.value = true
            accountListError.value = null

            await accountsApi.unpublishAccount(id)

            cacheStore.removeCache(`accounts:detail:${id}`)
            cacheStore.removeCache('accounts:stats')

            await Promise.all([
                loadAccountList(undefined, true),
                loadAccountStats(true)
            ])

            if (currentAccount.value?.id === id) {
                await loadAccountDetail(id, true)
            }

            return true
        } catch (error) {
            console.error('下架账号失败:', error)
            accountListError.value = error instanceof Error ? error.message : '下架账号失败'
            throw error
        } finally {
            accountOperationLoading.value = false
        }
    }

    // ==================== 分页操作 ====================

    /**
     * 加载下一页
     */
    const loadNextPage = async (): Promise<boolean> => {
        if (!hasNextPage.value) return false

        const nextPage = pagination.value.page + 1
        const params = { ...lastSearchQuery.value, page: nextPage }

        try {
            await loadAccountList(params)
            return true
        } catch (error) {
            console.error('加载下一页失败:', error)
            return false
        }
    }

    /**
     * 加载上一页
     */
    const loadPreviousPage = async (): Promise<boolean> => {
        if (!hasPreviousPage.value) return false

        const prevPage = pagination.value.page - 1
        const params = { ...lastSearchQuery.value, page: prevPage }

        try {
            await loadAccountList(params)
            return true
        } catch (error) {
            console.error('加载上一页失败:', error)
            return false
        }
    }

    /**
     * 跳转到指定页
     * @param page 页码
     */
    const goToPage = async (page: number): Promise<boolean> => {
        if (page < 1) return false

        const params = { ...lastSearchQuery.value, page }

        try {
            await loadAccountList(params)
            return true
        } catch (error) {
            console.error('跳转页面失败:', error)
            return false
        }
    }

    /**
     * 刷新账号列表
     */
    const refreshAccountList = async (): Promise<boolean> => {
        try {
            await loadAccountList(lastSearchQuery.value, true)
            return true
        } catch (error) {
            console.error('刷新账号列表失败:', error)
            return false
        }
    }

    // ==================== 工具方法 ====================

    /**
     * 根据ID查找账号
     * @param id 账号ID
     */
    const findAccountById = (id: number): AccountListItem | null => {
        return accountList.value.find(account => account.id === id) || null
    }

    /**
     * 根据状态获取账号列表
     * @param status 账号状态
     */
    const getAccountsByStatus = (status: AccountStatusEnum): AccountListItem[] => {
        return accountList.value.filter(account => account.status === status)
    }

    /**
     * 重置搜索参数
     */
    const resetSearchQuery = (): void => {
        lastSearchQuery.value = {}
    }

    /**
     * 清除错误信息
     */
    const clearAccountListError = (): void => {
        accountListError.value = null
    }

    /**
     * 清除当前账号
     */
    const clearCurrentAccount = (): void => {
        currentAccount.value = null
    }

    // ==================== 统计数据操作 ====================

    /**
     * 加载账号统计数据
     * @param forceRefresh 是否强制刷新
     */
    const loadAccountStats = async (forceRefresh = false): Promise<void> => {
        try {
            accountStatsLoading.value = true
            accountStatsError.value = null

            const cacheKey = 'accounts:stats'
            const data = await cacheStore.smartCache(
                cacheKey,
                () => accountsApi.getAccountStats(),
                forceRefresh
            )

            accountStats.value = data
        } catch (error) {
            console.error('加载账号统计失败:', error)
            accountStatsError.value = error instanceof Error ? error.message : '加载账号统计失败'
        } finally {
            accountStatsLoading.value = false
        }
    }

    /**
     * 清除统计数据错误
     */
    const clearAccountStatsError = (): void => {
        accountStatsError.value = null
    }

    // ==================== 初始化 ====================

    /**
     * 初始化账号数据
     */
    const initAccounts = async (): Promise<void> => {
        try {
            await loadAccountList()
        } catch (error) {
            console.error('初始化账号数据失败:', error)
        }
    }

    // ==================== 导出 ====================

    return {
        // 状态
        accountList: computed(() => accountList.value),
        pagination: computed(() => pagination.value),
        lastSearchQuery: computed(() => lastSearchQuery.value),
        currentAccount: computed(() => currentAccount.value),
        accountStats: computed(() => accountStats.value),
        accountListLoading: computed(() => accountListLoading.value),
        accountDetailLoading: computed(() => accountDetailLoading.value),
        accountOperationLoading: computed(() => accountOperationLoading.value),
        accountStatsLoading: computed(() => accountStatsLoading.value),
        accountListError: computed(() => accountListError.value),
        accountStatsError: computed(() => accountStatsError.value),

        // 计算属性
        hasAccountList,
        accountCount,
        currentPage,
        pageSize,
        hasNextPage,
        hasPreviousPage,
        accountListByStatus,
        availableAccountList,
        myAccountList,

        // 数据加载
        loadAccountList,
        searchAccounts,
        loadAccountDetail,
        refreshAccountList,
        loadAccountStats,
        
        // 市场数据加载（公开访问）
        searchMarketAccounts,
        loadMarketAccountDetail,

        // 数据操作
        createAccount,
        updateAccount,
        deleteAccount,
        submitForAudit,
        cancelAudit,
        unpublishAccount,

        // 分页操作
        loadNextPage,
        loadPreviousPage,
        goToPage,

        // 工具方法
        findAccountById,
        getAccountsByStatus,
        resetSearchQuery,
        clearAccountListError,
        clearCurrentAccount,
        clearAccountStatsError,

        // 初始化
        initAccounts
    }
}) 
